import { useMemo } from 'react'
import { Image, Platform, View } from 'react-native'
import { SvgXml } from 'react-native-svg'
import { LinearGradient } from 'expo-linear-gradient'
import { createAvatar } from '@dicebear/core'
import { getInitials } from '@ppwr/poker'
import { DS } from '@/design-system'

// Lazy style imports to avoid loading unused DiceBear packages
const STYLES = {
  shapes: () => require('@dicebear/shapes'),
  rings: () => require('@dicebear/rings'),
  thumbs: () => require('@dicebear/thumbs'),
  funEmoji: () => require('@dicebear/fun-emoji'),
} as const

type DiceBearStyle = keyof typeof STYLES

type AvatarConfig =
  | { type: 'dicebear'; style: DiceBearStyle }
  | { type: 'gradient'; color1: string; color2: string }
  | { type: 'solid'; color: string }

type LoungeAvatarProps = {
  name: string
  branding: { logoThumbS3Key?: string; avatar?: AvatarConfig }
  size: number
  backgroundColor?: string
  textColor?: string
}

const S3_BASE_URL = 'https://assets.ppwr.io'

function generateDiceBearSvg(style: DiceBearStyle, seed: string): string {
  const styleModule = STYLES[style]()
  return createAvatar(styleModule, { seed }).toString()
}

/** `react-native-svg-web` (Metro alias on web) does not export SvgXml. */
function DiceBearSvgXml({ svg, size }: { svg: string; size: number }) {
  if (Platform.OS === 'web') {
    const uri = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
    return <Image source={{ uri }} style={{ width: size, height: size }} resizeMode="cover" />
  }
  return <SvgXml xml={svg} width={size} height={size} />
}

function Initials({ name, size, color = '#fff' }: { name: string; size: number; color?: string }) {
  return (
    <DS.Text
      variant="Body Large/Medium"
      style={{ color, fontSize: size * 0.4, lineHeight: size * 0.5 }}
    >
      {getInitials(name)}
    </DS.Text>
  )
}

export function LoungeAvatar({ name, branding, size, backgroundColor, textColor }: LoungeAvatarProps) {
  const borderRadius = size / 2
  const avatar = branding.avatar

  const diceBearStyle: DiceBearStyle | null =
    avatar?.type === 'dicebear'
      ? avatar.style in STYLES
        ? (avatar.style as DiceBearStyle)
        : 'shapes'
      : null

  const diceBearSvg = useMemo(
    () => (diceBearStyle ? generateDiceBearSvg(diceBearStyle, name) : ''),
    [diceBearStyle, name]
  )

  // S3 logo takes top precedence
  if (branding.logoThumbS3Key) {
    return (
      <View style={{ width: size, height: size, borderRadius, overflow: 'hidden' }}>
        <Image
          source={{ uri: `${S3_BASE_URL}/${branding.logoThumbS3Key}` }}
          style={{ width: size, height: size }}
          resizeMode="cover"
        />
      </View>
    )
  }

  // Gradient
  if (avatar?.type === 'gradient') {
    return (
      <View style={{ width: size, height: size, borderRadius, overflow: 'hidden' }}>
        <LinearGradient
          colors={[avatar.color1, avatar.color2]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={{ width: size, height: size, justifyContent: 'center', alignItems: 'center' }}
        >
          <Initials name={name} size={size} color={textColor} />
        </LinearGradient>
      </View>
    )
  }

  // Solid color
  if (avatar?.type === 'solid') {
    return (
      <View
        style={{
          width: size,
          height: size,
          borderRadius,
          backgroundColor: avatar.color,
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Initials name={name} size={size} color={textColor} />
      </View>
    )
  }

  // DiceBear — explicit config
  if (avatar?.type === 'dicebear') {
    return (
      <View style={{ width: size, height: size, borderRadius, overflow: 'hidden' }}>
        <DiceBearSvgXml svg={diceBearSvg} size={size} />
      </View>
    )
  }

  // Default — maroon with initials (overridable via backgroundColor / textColor props)
  return (
    <View
      style={{
        width: size,
        height: size,
        borderRadius,
        backgroundColor: backgroundColor ?? DS.palette['Brand/Maroon'],
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      <Initials name={name} size={size} color={textColor ?? '#fff'} />
    </View>
  )
}
